/**
 * Compact 7-day strip (Sunday-start). ONE row: slim ‹ › paging touchables flank
 * the seven day cells — there is no header row and no range label, because the
 * date band directly beneath the strip already names the selected day in full.
 *
 * Today gets a focus-color ring; the selected day gets a translucent blue fill
 * with interactive.primaryHover ink (NOT onColor — the fill is translucent, see
 * the onColor docs; NOT text.link — it fails 4.5:1 on the tint in light), which
 * takes over from the ring on a day that is both (MonthGrid's rule too).
 *
 * Dots mean "there is still work here": `countsByKey` carries INCOMPLETE todos
 * only, so a fully-ticked day goes dotless. The row shows min(count, 3) of
 * them, so a heavy day reads heavier at a glance without the strip having to
 * carry a number. They render on the SELECTED day too — the fill says which day
 * is open, the dots say how much is on it, and hiding them mid-selection made
 * the strip flicker as the reader moved along it.
 *
 * Day cells borrow MonthGrid's shared formatDayAccessibilityLabel — the Planner
 * grid is fed the same remaining-only counts, so the two calendars must
 * announce a date alike.
 */
import React from 'react';
import { View, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import Icon from 'react-native-vector-icons/Ionicons';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { useTypography } from '@/hooks/shared/useTypography';
import { useSpacing } from '@/hooks/shared/useSpacing';
import { useRadius } from '@/hooks/shared/useRadius';
import {
  formatDayAccessibilityLabel,
  toDateKey,
} from '@/utils/residents/todoCalendar';

const WEEKDAY_INITIALS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

/**
 * Dot geometry, below the spacing scale on purpose: these are 5pt marks with a
 * 2pt gap, and the smallest spacing step (4) would blow the three-dot row out
 * past a ~35pt cell.
 */
const DOT_SIZE = 5;
const DOT_GAP = 2;
/** Past three, more dots stop reading as a count and start reading as noise. */
const MAX_DOTS = 3;

/** Shared per-dot id; scope a search to the day's `todo-dot-<key>` row. */
export const WEEK_STRIP_DOT_TEST_ID = 'todo-dot-mark';

/** The paging glyph, in points. Small by design — the hitSlop below is the
 *  actual target. */
const CHEVRON_SIZE = 16;
/** 14pt top/bottom takes the ~16pt glyph past the 44pt vertical floor. */
const CHEVRON_SLOP_Y = 14;
/**
 * OUTWARD chevron slop: the card's own 16pt inset, which is dead space either
 * way. It stops there because a touch beyond the card is outside this row's
 * ancestors, and neither platform routes those down to a child.
 */
const CHEVRON_SLOP_OUT = 16;

interface WeekStripProps {
  weekDays: Date[];
  selectedKey: string;
  todayKey: string;
  /**
   * Remaining (incomplete) todos per date key — the same number the dots stand
   * for. A day whose todos are all ticked off reads as 0 and shows no dot, so
   * the strip answers "where is there still work?" at a glance.
   */
  countsByKey: Record<string, number>;
  onSelectDay: (d: Date) => void;
  onPrevWeek: () => void;
  onNextWeek: () => void;
}

export const WeekStrip: React.FC<WeekStripProps> = React.memo(
  ({
    weekDays,
    selectedKey,
    todayKey,
    countsByKey,
    onSelectDay,
    onPrevWeek,
    onNextWeek,
  }) => {
    const type = useTypography();
    const space = useSpacing();
    const radius = useRadius();

    /**
     * INWARD slop — the share of a gap each touch target may claim.
     *
     * Every neighbouring pair in this row (chevron↔cell and cell↔cell) is
     * separated by exactly one space.xs gap, and each side takes HALF of it, so
     * the slop-expanded rects MEET but never overlap.
     *
     * That is the whole point. Both platforms hit-test siblings in REVERSE
     * order, and the order here is not symmetric: the container renders
     * [prev chevron, daysRow, next chevron], so a next-chevron/Saturday overlap
     * resolves to the CHEVRON — a tap aimed at Saturday's right edge would page
     * the week forward and drag the selection +7 days with it — while the
     * mirror-image prev-chevron/Sunday overlap resolves to Sunday. Splitting
     * the gap takes render order out of it, and the strip's two edges behave
     * alike.
     */
    const gapShare = space.xs / 2;
    const prevHitSlop = {
      top: CHEVRON_SLOP_Y,
      bottom: CHEVRON_SLOP_Y,
      left: CHEVRON_SLOP_OUT,
      right: gapShare,
    };
    const nextHitSlop = {
      top: CHEVRON_SLOP_Y,
      bottom: CHEVRON_SLOP_Y,
      left: gapShare,
      right: CHEVRON_SLOP_OUT,
    };
    /**
     * The cells are ~55pt tall but only ~25pt wide on a 320pt screen — the two
     * chevrons and their gaps take 40pt out of the row the seven cells used to
     * have to themselves — so the width is the target deficit, and it is
     * horizontal slop that answers it. Half a gap a side is all a cell can take
     * without reaching into a neighbour it might beat on order alone.
     */
    const dayHitSlop = { left: gapShare, right: gapShare };

    const styles = useThemedStyles(({ colors, semantic }) =>
      StyleSheet.create({
        container: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: space.xs,
        },
        chevron: {
          flexShrink: 0,
          // A padded box pulled back by the same margin: the hover tint has a
          // rounded shape while the glyph (and the hitSlop maths) stay put.
          padding: space.xs,
          margin: -space.xs,
          borderRadius: radius.sm,
        },
        chevronIcon: {
          color: colors.text.secondary,
        },
        daysRow: {
          flex: 1,
          flexDirection: 'row',
          gap: space.xs,
        },
        dayCell: {
          flex: 1,
          alignItems: 'center',
          // space.xs, not space.sm: with the strip now always on, every point
          // of cell padding is height the todo list below does not get.
          paddingVertical: space.xs,
          borderRadius: radius.md,
          borderWidth: 1,
          borderColor: 'transparent',
          gap: space.xs,
        },
        dayCellToday: {
          borderColor: semantic.border.focus,
        },
        dayCellSelected: {
          backgroundColor: colors.accent.blueBgStrong,
          // Unchanged from before, and the same call MonthGrid makes: the fill
          // replaces the ring on a day that is both today and selected, rather
          // than stacking a blue line around a blue box.
          borderColor: 'transparent',
        },
        weekdayLabel: {
          ...type.captionMedium,
          color: semantic.text.secondary,
        },
        weekdayLabelSelected: {
          color: semantic.interactive.primaryHover,
        },
        dayNumber: {
          ...type.cardTitle,
          color: colors.text.secondary,
        },
        dayNumberToday: {
          color: colors.text.primary,
        },
        dayNumberSelected: {
          color: semantic.interactive.primaryHover,
        },
        dotRow: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: DOT_GAP,
          height: DOT_SIZE,
        },
        dot: {
          width: DOT_SIZE,
          height: DOT_SIZE,
          borderRadius: radius.full,
          backgroundColor: semantic.status.info,
        },
        // Holds the dot row's height on a clear day so the cells stay the same
        // size across the week.
        dotSpacer: {
          width: DOT_SIZE,
          height: DOT_SIZE,
        },
      }),
    );

    return (
      <View style={styles.container}>
        <Hoverable
          hover="row"
          style={styles.chevron}
          onPress={onPrevWeek}
          hitSlop={prevHitSlop}
          accessibilityRole="button"
          accessibilityLabel="Previous week"
        >
          <Icon
            name="chevron-back"
            size={CHEVRON_SIZE}
            style={styles.chevronIcon}
          />
        </Hoverable>
        <View style={styles.daysRow}>
          {weekDays.map((day, index) => {
            const key = toDateKey(day);
            const isSelected = key === selectedKey;
            const isToday = key === todayKey;
            const count = countsByKey[key] ?? 0;
            const dotCount = Math.min(count, MAX_DOTS);
            return (
              <Hoverable
                key={key}
                // The selected day keeps its blue fill: `none` is cursor only.
                hover={isSelected ? 'none' : 'row'}
                style={[
                  styles.dayCell,
                  isToday && styles.dayCellToday,
                  isSelected && styles.dayCellSelected,
                ]}
                onPress={() => onSelectDay(day)}
                hitSlop={dayHitSlop}
                accessibilityRole="button"
                accessibilityState={{ selected: isSelected }}
                accessibilityLabel={formatDayAccessibilityLabel(day, count)}
              >
                <Text
                  scale="micro"
                  style={[
                    styles.weekdayLabel,
                    isSelected && styles.weekdayLabelSelected,
                  ]}
                >
                  {WEEKDAY_INITIALS[index]}
                </Text>
                <Text
                  scale="micro"
                  style={[
                    styles.dayNumber,
                    isToday && styles.dayNumberToday,
                    isSelected && styles.dayNumberSelected,
                  ]}
                >
                  {String(day.getDate())}
                </Text>
                {dotCount > 0 ? (
                  <View style={styles.dotRow} testID={`todo-dot-${key}`}>
                    {Array.from({ length: dotCount }, (_, i) => (
                      <View
                        key={i}
                        style={styles.dot}
                        testID={WEEK_STRIP_DOT_TEST_ID}
                      />
                    ))}
                  </View>
                ) : (
                  <View style={styles.dotSpacer} />
                )}
              </Hoverable>
            );
          })}
        </View>
        <Hoverable
          hover="row"
          style={styles.chevron}
          onPress={onNextWeek}
          hitSlop={nextHitSlop}
          accessibilityRole="button"
          accessibilityLabel="Next week"
        >
          <Icon
            name="chevron-forward"
            size={CHEVRON_SIZE}
            style={styles.chevronIcon}
          />
        </Hoverable>
      </View>
    );
  },
);

WeekStrip.displayName = 'WeekStrip';
